/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { ClientProfile, User } from "api/types";
import classNames from "classnames";
import ReusableForm from "components/Form";
import { FieldType, FormData } from "components/types";
import { UseFormGetValues, UseFormSetValue, useForm } from "react-hook-form";
import {
  GENDER_OPTIONS,
  TITLE_OPTIONS,
  validateAustralianPhoneNumber,
  validateAustralianTelephoneNumber
} from "utils/util";
import { Option } from "components/Select";

export interface AdminUpdateProps {
  user: User;
  onSubmitFn: (data: FormData) => Promise<void>;
  isSuperAdmin?: boolean;
  refreshData?: () => void;
}

export const getContactDetailsFields = (
  getValue: UseFormGetValues<FormData>,
  setValue: UseFormSetValue<FormData>,
  clientProfile: ClientProfile,
  user: User,
  setResidentialState: (value: string | undefined) => void,
  residentialState: string | undefined,
  watchedFields: FormData,
  titleOptions: Option[],
  onChangeTitle: (title: {label: String, value: String}) => void
) => [
  {
    type: FieldType.Paragraph,
    parentFormContainerClassName: "w-full",
    name: "",
    label: "",
    children: (
      <p
        className={classNames(
          "mt-5 text-sm leading-5 font-semibold text-gray-700"
        )}
      >
        Client contact details
      </p>
    ),
  },
  {
    name: "mobile",
    label: "Mobile *",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.mobile || "",
    subType: "number",
    validationFunction: validateAustralianPhoneNumber,
    validationErrorMessage: "Invalid Australian phone number",
    requiredCondition: watchedFields && watchedFields.mobile,
  },
  {
    name: "telephoneNumber1",
    label: "Telephone number 1",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    validationFunction: validateAustralianTelephoneNumber,
    validationErrorMessage: "Invalid Australian telephone number",
    requiredCondition:
      Boolean(watchedFields && watchedFields.telephoneNumber1) &&
      "Invalid Australian telephone number",
    defaultValue: clientProfile.telephoneNumber1 || "",
  },
  {
    name: "telephoneNumber2",
    label: "Telephone number 2",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    validationFunction: validateAustralianTelephoneNumber,
    validationErrorMessage: "Invalid Australian telephone number",
    requiredCondition:
      Boolean(watchedFields && watchedFields.telephoneNumber2) &&
      "Invalid Australian telephone number",
    defaultValue: clientProfile.telephoneNumber2 || "",
  },
  {
    name: "preferredNumber",
    label: "Preferred number",
    type: FieldType.Select,
    options: [
      { label: "Mobile", value: "Mobile" },
      { label: "Telephone 1", value: "Telephone 1" },
      { label: "Telephone 2", value: "Telephone 2" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    hideSearch: true,
    defaultValue: clientProfile.preferredNumber || "",
  },
  {
    name: "email",
    label: "Email *",
    type: FieldType.EmailInput,
    placeholder: "johndoe@gmail.com",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.email,
  },
  {
    type: FieldType.Paragraph,
    parentFormContainerClassName: "w-full",
    name: "",
    label: "",
    children: (
      <p
        className={classNames(
          "mt-10 mb-3 text-sm leading-5 font-semibold text-gray-700"
        )}
      >
        Primary contact details
      </p>
    ),
  },
  {
    name: "sameAsClientDetails",
    label: "Same as client details",
    type: FieldType.Checbox,
    parentFormContainerClassName:
      "md:w-1/1 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.sameAsClientDetails,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setValue("pGender", clientProfile.gender);
        setValue("pTitle", user.title);
        setValue("pFirstName", user.firstName);
        setValue("pLastName", user.lastName);
        setValue("pMobile", getValue("mobile"));
        setValue("pTelephoneNumber1", getValue("telephoneNumber1"));
        setValue("pTelephoneNumber2", getValue("telephoneNumber2"));
        setValue("pPreferredNumber", getValue("preferredNumber"));
        setValue("pEmail", getValue("email"));
      } else {
        setValue("pGender", "");
        setValue("pTitle", "");
        setValue("pFirstName", "");
        setValue("pLastName", "");
        setValue("pMobile", "");
        setValue("pTelephoneNumber1", "");
        setValue("pTelephoneNumber2", "");
        setValue("pPreferredNumber", "");
        setValue("pEmail", "");
      }
    }
  },
  {
    name: "pGender",
    label: "Gender *",
    type: FieldType.Radio,
    options: GENDER_OPTIONS,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.pGender || "",
  },
  {
    name: "pTitle",
    label: "Title *",
    type: FieldType.Select,
    placeholder: "Select",
    options: titleOptions,
    onChange: onChangeTitle,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    hideSearch: true,
    defaultValue: clientProfile.pTitle || "",
  },
  {
    name: "pFirstName",
    label: "First Name",
    type: FieldType.Input,
    placeholder: "John",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.pFirstName || "",
  },
  {
    name: "pLastName",
    label: "Last Name",
    type: FieldType.Input,
    placeholder: "Doe",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.pLastName || "",
  },
  {
    name: "pRelationship",
    label: "Relationship",
    type: FieldType.Input,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.pRelationship || "",
  },
  {
    type: FieldType.Paragraph,
    parentFormContainerClassName: "w-full",
    name: "",
    label: "",
  },
  {
    name: "pMobile",
    label: "Mobile *",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    defaultValue: clientProfile.pMobile || "",
    validationFunction: validateAustralianPhoneNumber,
    validationErrorMessage: "Invalid Australian phone number",
    requiredCondition: watchedFields && watchedFields.pMobile,
  },
  {
    name: "pTelephoneNumber1",
    label: "Telephone number 1",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    validationFunction: validateAustralianTelephoneNumber,
    validationErrorMessage: "Invalid Australian telephone number",
    requiredCondition: Boolean(watchedFields && watchedFields.telephoneNumber1) && "Invalid Australian telephone number",
    defaultValue: clientProfile.pTelephoneNumber1 || "",
  },
  {
    name: "pTelephoneNumber2",
    label: "Telephone number 2",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    validationFunction: validateAustralianTelephoneNumber,
    validationErrorMessage: "Invalid Australian telephone number",
    requiredCondition: Boolean(watchedFields && watchedFields.telephoneNumber2) && "Invalid Australian telephone number",
    defaultValue: clientProfile.pTelephoneNumber2 || "",
  },
  {
    name: "pPreferredNumber",
    label: "Preferred number",
    type: FieldType.Select,
    options: [
      { label: "Mobile", value: "Mobile" },
      { label: "Telephone 1", value: "Telephone 1" },
      { label: "Telephone 2", value: "Telephone 2" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    hideSearch: true,
    defaultValue: clientProfile.pPreferredNumber || "",
  },
  {
    name: "pEmail",
    label: "Email *",
    type: FieldType.EmailInput,
    placeholder: "johndoe@gmail.com",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.pEmail || "",
  },
];

export default function AdminContactDetails({
  user,
  onSubmitFn,
  isSuperAdmin,
}: AdminUpdateProps) {
  const { formState, getValues, setValue, watch, ...formMethods } =
    useForm<FormData>();
  const { clientProfile } = user;
  const [residentialState, setResidentialState] = useState<string | undefined>(
    undefined
  );

  const watchedFields = watch();
  const selectedGender = watchedFields.pGender;
  const selectedTitle = watchedFields.pTitle;  

  const onSubmit = async (data: FormData) => onSubmitFn(data);
  
    /* ---------- FILTER TITLE BASED ON GENDER ---------- */
    const filteredTitleOptions = React.useMemo(() => {
      if (selectedGender === "Male") {
        return TITLE_OPTIONS.filter(o =>
          [ "Dr", "Mr"].includes(o.value)
        );
      }
  
      if (selectedGender === "Female") {
        return TITLE_OPTIONS.filter(o =>
          ["Dr", "Mrs", "Miss"].includes(o.value)
        );
      }
  
      return TITLE_OPTIONS;
    }, [selectedGender]);
  
  /* ---------- RESET INVALID VALUES ---------- */
  useEffect(() => {
    if (
      selectedTitle &&
      !filteredTitleOptions.some(o => o.value === selectedTitle)
    ) {
      setValue("pTitle", "");
    }
  }, [filteredTitleOptions, selectedTitle, setValue]);

  const onChangeTitle = (title: {label: String, value: String}) => {
    if (title.value === "Mr") {
      setValue("pGender", "Male");
    } else if (title.value === "Mrs" || title.value === "Miss") {
      setValue("pGender", "Female");
    }
  }
  
  return (
    <div className="bg-white rounded-md">
      <div className="pl-6 pr-0 pb-0">
        <ReusableForm
          fields={
            getContactDetailsFields(
              getValues,
              setValue,
              clientProfile,
              user,
              setResidentialState,
              residentialState,
              watchedFields,
              filteredTitleOptions,
              onChangeTitle
            )
          }
          onSubmit={onSubmit}
          id="edit-CONTACT_DETAILS"
          formClassName="pt-1 pb-6"
          formMethods={{
            ...formMethods,
            formState,
            getValues,
            setValue,
            watch,
          }}
        />
      </div>
    </div>
  );
}
